// In-memory dictionary: the static bundle with D1 changes (GET /api/dictionary/changes) merged
// over it. Built once per bundle/overlay change; everything else reads these maps.
import type { DictionaryChanges } from '@valyrio/engine/api';
import type { DictionaryBundle, Example, Form, Gloss, GlossLang, GlossStatus, Lemma } from '@valyrio/engine/types';
import { isFiChecked, isFiDraft } from '@valyrio/engine/types';
// citationForm lives in ./citation (it needs the paradigm generator, which start-up does not).
export { citationForm } from './citation';

/** Accumulated changes since the bundle, keyed by id, so repeated pulls merge cleanly. */
export interface DictOverlay {
  since: string; // `now` of the last successful pull
  lemmas: Record<string, Lemma>;
  glosses: Record<string, Gloss>;
  examples: Record<string, Example>;
  forms: Record<string, Form[]>; // lemmaId → override forms (replaces the bundle's)
  removed: string[];
  /** Glosses / examples deleted on the server (tombstones); absent in overlays saved before. */
  removedGlosses?: string[];
  removedExamples?: string[];
}

export function emptyOverlay(since: string): DictOverlay {
  return { since, lemmas: {}, glosses: {}, examples: {}, forms: {}, removed: [], removedGlosses: [], removedExamples: [] };
}

export function mergeChanges(o: DictOverlay, c: DictionaryChanges): DictOverlay {
  const next: DictOverlay = {
    since: c.now,
    lemmas: { ...o.lemmas },
    glosses: { ...o.glosses },
    examples: { ...o.examples },
    forms: { ...o.forms },
    removed: [...o.removed],
  };
  // Deletes first, then upserts: a row deleted and re-added in the same window is live.
  const goneGlosses = new Set(o.removedGlosses ?? []);
  const goneExamples = new Set(o.removedExamples ?? []);
  for (const id of c.removedGlossIds ?? []) {
    goneGlosses.add(id);
    delete next.glosses[id];
  }
  for (const id of c.removedExampleIds ?? []) {
    goneExamples.add(id);
    delete next.examples[id];
  }
  for (const l of c.lemmas) next.lemmas[l.id] = l;
  for (const g of c.glosses) {
    next.glosses[g.id] = g;
    goneGlosses.delete(g.id);
  }
  for (const e of c.examples) {
    next.examples[e.id] = e;
    goneExamples.delete(e.id);
  }
  next.removedGlosses = [...goneGlosses];
  next.removedExamples = [...goneExamples];
  for (const f of c.forms) next.forms[f.lemmaId] = f.forms;
  const removed = new Set(next.removed);
  for (const id of c.removedLemmaIds) {
    removed.add(id);
    delete next.lemmas[id];
  }
  // A lemma re-added after removal is live again.
  for (const l of c.lemmas) removed.delete(l.id);
  next.removed = [...removed];
  return next;
}

export interface Dictionary {
  /** The static bundle and the merged overlay this was built from (the engine Dictionary in
   * lib/lexicon.ts is built from the same pair). */
  bundle: DictionaryBundle;
  overlay: DictOverlay | null;
  version: string;
  generatedAt: string;
  lemmas: Map<string, Lemma>;
  /** Lemmas in bundle order (the fallback course order). */
  order: string[];
  glosses: Map<string, Gloss[]>; // lemmaId → glosses (both languages, by sense)
  examples: Map<string, Example>;
  examplesByLemma: Map<string, Example[]>;
}

export function buildDictionary(bundle: DictionaryBundle, overlay?: DictOverlay | null): Dictionary {
  const removed = new Set(overlay?.removed ?? []);
  const lemmas = new Map<string, Lemma>();
  const order: string[] = [];
  for (const l of bundle.lemmas) {
    if (removed.has(l.id) || l.status === 'removed') continue;
    lemmas.set(l.id, l);
    order.push(l.id);
  }
  if (overlay) {
    for (const l of Object.values(overlay.lemmas)) {
      if (!lemmas.has(l.id)) order.push(l.id);
      lemmas.set(l.id, l);
    }
    for (const [lemmaId, forms] of Object.entries(overlay.forms)) {
      const l = lemmas.get(lemmaId);
      if (l?.paradigm) lemmas.set(lemmaId, { ...l, paradigm: { ...l.paradigm, forms } });
    }
  }

  const glossById = new Map<string, Gloss>();
  const goneGlosses = new Set(overlay?.removedGlosses ?? []);
  const goneExamples = new Set(overlay?.removedExamples ?? []);
  for (const g of bundle.glosses) if (!goneGlosses.has(g.id)) glossById.set(g.id, g);
  if (overlay) for (const g of Object.values(overlay.glosses)) glossById.set(g.id, g);
  const glosses = new Map<string, Gloss[]>();
  for (const g of glossById.values()) {
    if (!lemmas.has(g.lemmaId)) continue;
    const list = glosses.get(g.lemmaId) ?? [];
    list.push(g);
    glosses.set(g.lemmaId, list);
  }
  for (const list of glosses.values()) list.sort((a, b) => a.sense - b.sense);

  const examples = new Map<string, Example>();
  for (const e of bundle.examples) if (!goneExamples.has(e.id)) examples.set(e.id, e);
  if (overlay) for (const e of Object.values(overlay.examples)) examples.set(e.id, e);
  const examplesByLemma = new Map<string, Example[]>();
  for (const e of examples.values()) {
    for (const id of e.lemmaIds) {
      const list = examplesByLemma.get(id) ?? [];
      list.push(e);
      examplesByLemma.set(id, list);
    }
  }

  return { bundle, overlay: overlay ?? null, version: bundle.version, generatedAt: bundle.generatedAt, lemmas, order, glosses, examples, examplesByLemma };
}

/**
 * The bundle without the rows deleted on the server (the engine Dictionary only knows lemma
 * removals, so the lexicon is built from this; the same object while nothing was deleted).
 */
export function bundleWithout(bundle: DictionaryBundle, overlay: DictOverlay | null): DictionaryBundle {
  const g = new Set(overlay?.removedGlosses ?? []);
  const e = new Set(overlay?.removedExamples ?? []);
  if (!g.size && !e.size) return bundle;
  return {
    ...bundle,
    glosses: g.size ? bundle.glosses.filter((x) => !g.has(x.id)) : bundle.glosses,
    examples: e.size ? bundle.examples.filter((x) => !e.has(x.id)) : bundle.examples,
  };
}

/** The overlay as a DictionaryChanges batch (for the engine Dictionary; applying it twice is harmless). */
export function overlayAsChanges(o: DictOverlay): DictionaryChanges {
  return {
    lemmas: Object.values(o.lemmas),
    glosses: Object.values(o.glosses),
    examples: Object.values(o.examples),
    forms: Object.entries(o.forms).map(([lemmaId, forms]) => ({ lemmaId, forms })),
    removedLemmaIds: [...o.removed],
    now: o.since,
  };
}

export function glossesFor(d: Dictionary, lemmaId: string, lang: GlossLang): Gloss[] {
  return (d.glosses.get(lemmaId) ?? []).filter((g) => g.lang === lang);
}

/** "nainen; vaimo" — senses joined. */
export function glossText(d: Dictionary, lemmaId: string, lang: GlossLang): string {
  return glossesFor(d, lemmaId, lang)
    .map((g) => g.text)
    .join('; ');
}

/**
 * The least certain status among Finnish texts shown together: 'draft' when any is an unreviewed
 * AI draft, else 'checked' when any is only AI-checked, else 'reviewed' (also for none).
 */
export function weakestFiStatus(rows: readonly { status?: GlossStatus | undefined }[]): GlossStatus {
  if (rows.some((g) => isFiDraft(g.status))) return 'draft';
  if (rows.some((g) => isFiChecked(g.status))) return 'checked';
  return 'reviewed';
}

/** The badge status of a lemma's Finnish meanings (see weakestFiStatus). */
export function finnishStatus(d: Dictionary, lemmaId: string): GlossStatus {
  return weakestFiStatus(glossesFor(d, lemmaId, 'fi'));
}

/** Best example for a card: show-tagged first, then any linked example. */
export function bestExample(d: Dictionary, lemmaId: string): Example | undefined {
  const lemma = d.lemmas.get(lemmaId);
  const fromSenses = (lemma?.senses ?? [])
    .flatMap((s) => s.exampleIds)
    .map((id) => d.examples.get(id))
    .filter((e): e is Example => !!e);
  const linked = d.examplesByLemma.get(lemmaId) ?? [];
  const all = [...fromSenses, ...linked.filter((e) => !fromSenses.includes(e))];
  return all.find((e) => e.show && e.show !== 'other') ?? all[0];
}
